import { useEffect, useRef } from 'react';
import { useBillingSummary } from '../billing/BillingSummaryProvider';
import { useAlertSource, useAlerts } from './AlertsProvider';
import { AppAlert } from './alertStore';

export function AccountAlerts({
  userId,
  purchaseReturn,
  openBilling,
}: {
  userId: string;
  purchaseReturn?: 'returned' | 'cancelled';
  openBilling: () => void;
}) {
  const { snapshot } = useBillingSummary(userId);
  const alerts = useAlerts();
  const openBillingRef = useRef(openBilling);
  openBillingRef.current = openBilling;
  const entries: AppAlert[] = [];
  const action = { label: 'Open billing', run: openBilling, navigate: true };
  if (snapshot.billing?.reconciliationPending)
    entries.push({
      id: 'billing-reconciliation',
      title: 'Billing changes paused',
      message: 'Account state is being reconciled.',
      severity: 'warning',
      action,
    });
  if (snapshot.wallet?.reconciliationRequired)
    entries.push({
      id: 'account-review',
      title: 'Purchases and AI paused',
      message: 'This account is being reviewed.',
      severity: 'warning',
      action,
    });
  if (snapshot.billing?.itemBilling.restricted)
    entries.push({
      id: 'account-restricted',
      title: 'Adding and editing paused',
      message:
        'Your balance must reach zero or higher to restore editing. Monthly charges continue while billable items remain.',
      severity: 'warning',
      action,
    });
  if (snapshot.error)
    entries.push({
      id: 'billing-load',
      title: 'Billing status could not be refreshed',
      message: 'Open Billing to refresh your balance and account status.',
      severity: 'warning',
      action,
    });
  useAlertSource(userId, 'account', entries);
  useEffect(() => {
    if (purchaseReturn)
      alerts?.notice(userId, {
        id: 'checkout-return',
        title: 'Check purchase status',
        severity: 'info',
        message:
          purchaseReturn === 'returned'
            ? 'You returned from Checkout. Open Billing to check whether credits were added.'
            : 'Checkout was closed. Open Billing to check the purchase status.',
        action: {
          label: 'Open billing',
          run: () => openBillingRef.current(),
          navigate: true,
        },
      });
    // The return is a single event, not a recurring condition on each render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [alerts, userId, purchaseReturn]);
  return null;
}
